import type { MotionPrefs, TowerFamilyId, TowerIdleMotion } from './types'

const IDLE: Record<TowerFamilyId, TowerIdleMotion> = {
  fire: { spin: 0, pulseAmplitude: 0.035, pulseHz: 1.1 },
  frost: { spin: 0.35, pulseAmplitude: 0.02, pulseHz: 0.55 },
  cannon: { spin: 0, pulseAmplitude: 0.008, pulseHz: 0.35 },
  poison: { spin: 0.55, pulseAmplitude: 0.025, pulseHz: 0.7 },
  aa: { spin: 0.9, pulseAmplitude: 0.02, pulseHz: 1.4 },
  mage: { spin: 0.65, pulseAmplitude: 0.03, pulseHz: 0.85 }
}

export function towerFamilyId(definitionId: string): TowerFamilyId {
  if (
    definitionId === 'frost' ||
    definitionId === 'cannon' ||
    definitionId === 'poison' ||
    definitionId === 'aa' ||
    definitionId === 'mage'
  ) {
    return definitionId
  }
  return 'fire'
}

export function towerIdleMotion(
  definitionId: string,
  level: number,
  prefs: MotionPrefs
): TowerIdleMotion {
  const base = IDLE[towerFamilyId(definitionId)]
  if (prefs.reducedMotion) {
    return { spin: 0, pulseAmplitude: 0, pulseHz: 0 }
  }
  const levelBoost = 1 + Math.max(0, Math.min(3, level - 1)) * 0.08
  return {
    spin: base.spin * levelBoost,
    pulseAmplitude: base.pulseAmplitude * levelBoost,
    pulseHz: base.pulseHz
  }
}

/** Deterministic phase offset so towers don't pulse in lockstep. */
export function towerIdlePhase(towerId: string): number {
  let h = 0
  for (let i = 0; i < towerId.length; i += 1) h = (h * 31 + towerId.charCodeAt(i)) >>> 0
  return (h % 628) / 100
}

export function sampleTowerIdle(
  motion: TowerIdleMotion,
  simTimeSec: number,
  phase: number
): { scale: number; rotation: number } {
  const pulse =
    motion.pulseAmplitude <= 0
      ? 0
      : Math.sin(simTimeSec * motion.pulseHz * Math.PI * 2 + phase) * motion.pulseAmplitude
  return {
    scale: 1 + pulse,
    rotation: motion.spin * simTimeSec + phase * 0.15
  }
}

/** Fire kick scale target (yoyo back to idle). */
export function towerFireKickScale(definitionId: string, prefs: MotionPrefs): number {
  if (prefs.reducedMotion) return 1.04
  switch (towerFamilyId(definitionId)) {
    case 'fire':
      return 1.1
    case 'frost':
      return 0.92
    case 'cannon':
      return 1.06
    case 'poison':
      return 1.12
    case 'mage':
      return 1.09
    default:
      return 1.08
  }
}

export function towerFireKickSimMs(definitionId: string): number {
  switch (towerFamilyId(definitionId)) {
    case 'cannon':
      return 100
    case 'frost':
      return 90
    default:
      return 80
  }
}

export function towerUpgradeKickScale(prefs: MotionPrefs): number {
  return prefs.reducedMotion ? 1.08 : 1.18
}

export function towerSellKickScale(prefs: MotionPrefs): number {
  return prefs.reducedMotion ? 0.92 : 0.82
}
